/**
 * StartSessionScreen
 *
 * Onboarding entry — full-bleed welcome, then a step-by-step flow:
 *   1. Welcome → Start a new session, or Resume with code
 *   2. (resume) Enter code → load existing session
 *
 * No modal framing, no back button — this is the door into the experience,
 * not a dialog. Fills the viewport, lets the cream backdrop + nature
 * decoration breathe.
 */

import { useState, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, KeyRound, Sparkles } from 'lucide-react';
import { isValidCodeFormat, normalizeCode } from '../../lib/sessionCode';
import { GlobalNav } from '../layout/GlobalNav';

interface Props {
  language: 'en' | 'es';
  onBack: () => void;
  loading: boolean;
  loadError: string | null;
  onStartNew: () => void | Promise<void>;
  onResume: (code: string) => Promise<boolean>;
}

// ── Shared design tokens (mirror TeachingFacilitation.tsx) ──────────────
const INK = '#2a6d5b';
const INK_SOFT = '#355c46';
const BRAND_GREEN = '#42a57a';
const GOLD = '#c9a961';
const CREAM = '#fbfbe8';
const CREAM_DEEP = '#f4efde';
const CARD_BORDER = '#e8e0c8';
const MUTED = '#9a9a9a';
const SOFT_RED = '#b85a55';
const SERIF = "'Fraunces', Georgia, serif";
const SANS = "'Inter', system-ui, -apple-system, sans-serif";

const FLOATING_DECOR = ['🌿', '🌱', '🍃', '🦋', '🐝', '🌸', '🍂', '🌼'];

export function StartSessionScreen({
  language,
  onBack,
  loading,
  loadError,
  onStartNew,
  onResume,
}: Props) {
  const t = (en: string, es: string) => (language === 'en' ? en : es);
  const [mode, setMode] = useState<'welcome' | 'resume'>('welcome');
  const [codeInput, setCodeInput] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const displayError = loadError || localError;

  const handleResumeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizeCode(codeInput);
    if (!isValidCodeFormat(normalized)) {
      setLocalError(
        t(
          "That doesn't look like a valid code. Codes look like FOX-PINE-42.",
          'Ese código no parece válido. Los códigos tienen la forma FOX-PINE-42.',
        ),
      );
      return;
    }
    setLocalError(null);
    const ok = await onResume(normalized);
    if (!ok && !loadError) {
      setLocalError(
        t('Session not found or expired.', 'Sesión no encontrada o expirada.'),
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: CREAM }}
    >
      <GlobalNav onBackToHome={onBack} />

      {/* Atmospheric layers — subtle warm glow that complements cream rather
          than overpowering it. Fixed so they stay put while content scrolls. */}
      <div className="pointer-events-none fixed inset-0">
        <div
          className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full blur-3xl"
          style={{ background: `${GOLD}22` }}
        />
        <div
          className="absolute -bottom-44 -left-24 w-[640px] h-[640px] rounded-full blur-3xl"
          style={{ background: `${BRAND_GREEN}1f` }}
        />

        {/* Floating nature glyphs — quieter than before so they read as
            decoration, not signal. */}
        {FLOATING_DECOR.map((emoji, i) => (
          <motion.span
            key={i}
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{
              opacity: 0.6,
              y: [0, -12, 0],
            }}
            transition={{
              opacity: { duration: 1.4, delay: i * 0.12 },
              y: {
                duration: 5 + (i % 3),
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.4,
              },
            }}
            className="absolute select-none"
            style={{
              fontSize: 'clamp(20px, 2.6vw, 30px)',
              top: `${10 + (i * 11) % 75}%`,
              left: `${(i * 17 + 5) % 92}%`,
            }}
          >
            {emoji}
          </motion.span>
        ))}
      </div>

      {/* Foreground content */}
      <div className="relative min-h-screen flex flex-col items-center justify-center px-6 py-16">
        <AnimatePresence mode="wait">
          {mode === 'welcome' ? (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="w-full max-w-3xl text-center"
            >
              {/* Eyebrow chip — cream-deep with gold hairline */}
              <motion.div
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="inline-flex items-center gap-2 mb-8"
                style={{
                  padding: '8px 18px',
                  background: CREAM_DEEP,
                  border: `1px solid ${GOLD}55`,
                  borderRadius: 999,
                  color: INK,
                  fontFamily: SANS,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}
              >
                <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND_GREEN }} />
                {t('Wonderpath · Early Childhood', 'Wonderpath · Primera Infancia')}
              </motion.div>

              {/* Title — Fraunces light, ink-green, italic on the second line
                  so it reads like a warm spoken invitation. */}
              <h1
                style={{
                  fontFamily: SERIF,
                  fontWeight: 300,
                  fontSize: 'clamp(44px, 7vw, 88px)',
                  color: INK,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05,
                  marginBottom: 22,
                }}
              >
                {t('Welcome,', 'Bienvenido,')}
                <br />
                <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
                  {t('let’s teach together.', 'enseñemos juntos.')}
                </span>
              </h1>

              <p
                className="max-w-2xl mx-auto"
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontWeight: 500,
                  fontSize: 'clamp(17px, 2.2vw, 22px)',
                  color: INK_SOFT,
                  lineHeight: 1.55,
                  marginBottom: 40,
                }}
              >
                {t(
                  'A guided lesson companion for ages 3–6. We’ll prepare your class, walk with you through every phase, and help you reflect afterwards.',
                  'Un acompañante guiado para clases de 3 a 6 años. Preparamos tu clase, te acompañamos en cada fase y te ayudamos a reflexionar al terminar.',
                )}
              </p>

              {/* CTA pair — primary brand-green pill + outline secondary */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
                <button
                  type="button"
                  onClick={() => onStartNew()}
                  disabled={loading}
                  className="flex-1 inline-flex items-center justify-center gap-2"
                  style={{
                    background: BRAND_GREEN,
                    color: '#ffffff',
                    fontFamily: SANS,
                    fontSize: 17,
                    fontWeight: 600,
                    padding: '16px 28px',
                    borderRadius: 999,
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    boxShadow: '0 8px 26px rgba(66,165,122,0.32)',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                  }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {loading
                    ? t('Creating your session…', 'Creando tu sesión…')
                    : t('Begin a new session', 'Comenzar nueva sesión')}
                  {!loading && <ChevronRight className="w-5 h-5" />}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode('resume');
                    setLocalError(null);
                  }}
                  disabled={loading}
                  className="flex-1 inline-flex items-center justify-center gap-2"
                  style={{
                    background: 'transparent',
                    color: BRAND_GREEN,
                    border: `1.5px solid ${BRAND_GREEN}`,
                    fontFamily: SANS,
                    fontSize: 17,
                    fontWeight: 600,
                    padding: '14px 28px',
                    borderRadius: 999,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  <KeyRound className="w-5 h-5" strokeWidth={1.8} />
                  {t('I have a code', 'Tengo un código')}
                </button>
              </div>

              <p
                className="max-w-md mx-auto mt-10"
                style={{
                  fontFamily: SANS,
                  fontSize: 13,
                  fontWeight: 500,
                  color: MUTED,
                  lineHeight: 1.55,
                }}
              >
                {t(
                  'Sessions are anonymous — your code is your only way back. Beta sessions stay live for 90 days.',
                  'Las sesiones son anónimas — tu código es la única forma de volver. Las sesiones beta duran 90 días.',
                )}
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="resume"
              onSubmit={handleResumeSubmit}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="w-full max-w-xl text-center"
            >
              <div
                className="inline-flex items-center gap-2 mb-8"
                style={{
                  padding: '8px 18px',
                  background: CREAM_DEEP,
                  border: `1px solid ${GOLD}55`,
                  borderRadius: 999,
                  color: INK,
                  fontFamily: SANS,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}
              >
                <KeyRound className="w-3.5 h-3.5" style={{ color: BRAND_GREEN }} />
                {t('Step 2 · Enter your code', 'Paso 2 · Ingresa tu código')}
              </div>

              <h2
                style={{
                  fontFamily: SERIF,
                  fontWeight: 300,
                  fontSize: 'clamp(36px, 5.5vw, 60px)',
                  color: INK,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05,
                  marginBottom: 14,
                }}
              >
                {t('Welcome back.', 'Bienvenido de nuevo.')}
              </h2>
              <p
                className="mb-10"
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontWeight: 500,
                  fontSize: 'clamp(16px, 2vw, 20px)',
                  color: INK_SOFT,
                }}
              >
                {t(
                  'Type the session code you saved last time.',
                  'Escribe el código de sesión que guardaste la última vez.',
                )}
              </p>

              <input
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                autoCorrect="off"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value);
                  setLocalError(null);
                }}
                placeholder="FOX-PINE-42"
                className="w-full text-center uppercase focus:outline-none"
                style={{
                  padding: '20px 24px',
                  background: '#ffffff',
                  border: `1.5px solid ${INK}25`,
                  borderRadius: 18,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 24,
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  color: INK,
                  transition: 'border-color 160ms ease, box-shadow 160ms ease',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = BRAND_GREEN;
                  e.currentTarget.style.boxShadow = `0 0 0 4px ${BRAND_GREEN}25`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = `${INK}25`;
                  e.currentTarget.style.boxShadow = 'none';
                }}
                autoFocus
              />

              {displayError && (
                <p
                  role="alert"
                  className="mt-4 px-4 py-2.5"
                  style={{
                    background: `${SOFT_RED}15`,
                    color: SOFT_RED,
                    border: `1px solid ${SOFT_RED}55`,
                    borderRadius: 14,
                    fontFamily: SERIF,
                    fontStyle: 'italic',
                    fontWeight: 500,
                    fontSize: 15,
                  }}
                >
                  {displayError}
                </p>
              )}

              <div className="flex gap-3 mt-7">
                <button
                  type="button"
                  onClick={() => {
                    setMode('welcome');
                    setLocalError(null);
                    setCodeInput('');
                  }}
                  disabled={loading}
                  className="flex-1"
                  style={{
                    background: 'transparent',
                    color: BRAND_GREEN,
                    border: `1.5px solid ${BRAND_GREEN}`,
                    fontFamily: SANS,
                    fontSize: 16,
                    fontWeight: 600,
                    padding: '14px 22px',
                    borderRadius: 999,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  {t('Back', 'Volver')}
                </button>
                <button
                  type="submit"
                  disabled={loading || !codeInput.trim()}
                  className="flex-[2] inline-flex items-center justify-center gap-2"
                  style={{
                    background: BRAND_GREEN,
                    color: '#ffffff',
                    fontFamily: SANS,
                    fontSize: 16,
                    fontWeight: 600,
                    padding: '16px 24px',
                    borderRadius: 999,
                    border: 'none',
                    cursor: (loading || !codeInput.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (loading || !codeInput.trim()) ? 0.5 : 1,
                    boxShadow: '0 8px 26px rgba(66,165,122,0.32)',
                  }}
                >
                  {loading ? t('Loading…', 'Cargando…') : t('Resume session', 'Reanudar sesión')}
                  {!loading && <ChevronRight className="w-5 h-5" />}
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
